// =====================================================================
// PagePublique.jsx — Commandes prêtes v3 B (16-10-2026) : morceaux communs des pages PUBLIQUES
// /order/<jeton>/rdv et /order/<jeton>/documents (bilingues, plein écran, styles autonomes comme
// OrderPhotos.jsx : aucune barre Zeus).
// =====================================================================

import { useEffect } from 'react';

/** Jamais indexée ; langue anglaise (puis français dans le texte) ; titre de l'onglet. */
export function useMetaPublique(titre) {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    const lang = document.documentElement.lang;
    const avant = document.title;
    document.documentElement.lang = 'en';
    return () => { meta.remove(); document.documentElement.lang = lang; document.title = avant; };
  }, []);
  useEffect(() => { if (titre) document.title = titre; }, [titre]);
}

const unites = (u, en) => {
  const t = [];
  const p = u?.pallets || 0; const c = u?.parcels || 0;
  if (p) t.push(en ? `${p} pallet${p > 1 ? 's' : ''}` : `${p} palette${p > 1 ? 's' : ''}`);
  if (c) t.push(en ? `${c} parcel${c > 1 ? 's' : ''}` : `${c} colis`);
  return t.join(en ? ' and ' : ' et ');
};

export function Entete({ titreEn, titreFr, data }) {
  return (
    <>
      <div style={S.marque}>Brain E-Log</div>
      <h1 style={S.h1}>{`${titreEn} — ${data.orderRef}`}</h1>
      <div style={S.fr}>{titreFr}</div>
      <p style={S.petit}>
        Brain E-Log, Boulevard de la Technicité 1A, 7110 Houdeng-Goegnies
        {unites(data.units, true) ? ` · ${unites(data.units, true)} / ${unites(data.units, false)}` : ''}
      </p>
    </>
  );
}
export const Chargement = () => <div style={S.wrap}><div style={S.marque}>Brain E-Log</div><p style={S.petit}>Loading… / Chargement…</p></div>;
export const Invalide = () => (
  <div style={S.wrap}>
    <div style={S.marque}>Brain E-Log</div>
    <div role="alert" style={{ marginTop: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, fontSize: 17 }}>
      This link has expired or is invalid. / Ce lien a expiré ou n'est pas valide.
    </div>
  </div>
);
/** Première lecture impossible (réseau, serveur occupé…) — PAS « lien invalide » : on peut réessayer. */
export const ErreurChargement = ({ onReessayer }) => (
  <div style={S.wrap}>
    <div style={S.marque}>Brain E-Log</div>
    <div role="alert" style={{ marginTop: 16, background: '#fff1d9', borderRadius: 12, padding: 20, fontSize: 16 }}>
      The page could not be loaded (network or server busy). / La page n'a pas pu être chargée (réseau ou serveur occupé).
      <div style={{ marginTop: 12 }}><button type="button" onClick={onReessayer} style={S.bouton}>Try again / Réessayer</button></div>
    </div>
  </div>
);
export const Pied = () => <p style={{ ...S.petit, marginTop: 24 }}>Brain E-Log · Boulevard de la Technicité 1A, 7110 Houdeng-Goegnies, Belgium · Monday to Friday, 8:00–16:00</p>;

const C = { encre: '#17232a', doux: '#5b6b73', ligne: '#cfd8dc', accent: '#0f6e74', rdv: '#0e7a5a' };
export const S = {
  wrap: { maxWidth: 880, margin: '0 auto', padding: '20px 16px 48px', fontFamily: 'system-ui, -apple-system, "Segoe UI", Arial, sans-serif', color: C.encre, boxSizing: 'border-box', fontSize: 15, lineHeight: 1.5 },
  marque: { fontWeight: 800, color: C.accent, fontSize: 15, letterSpacing: '.01em' },
  h1: { fontSize: 'clamp(1.2rem, 3.4vw, 1.6rem)', margin: '10px 0 0', lineHeight: 1.2, overflowWrap: 'anywhere' },
  fr: { color: C.doux },
  petit: { color: C.doux, fontSize: 13, margin: '4px 0' },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: C.doux, marginBottom: 4 },
  form: { border: 0, padding: 0, margin: '16px 0 0', display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 },
  ligne: { display: 'flex', flexWrap: 'wrap', gap: 12 },
  champ: { flex: '1 1 200px', minWidth: 0 },
  input: { font: 'inherit', fontSize: 16, color: C.encre, background: '#fff', border: `1px solid ${C.ligne}`, borderRadius: 8, padding: '9px 10px', width: '100%', boxSizing: 'border-box', minHeight: 44 },
  jours: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: 6 },
  jour: { font: 'inherit', fontSize: 14, fontWeight: 700, border: `1px solid ${C.ligne}`, borderRadius: 8, padding: '8px 4px', background: '#fff', color: C.encre, cursor: 'pointer', minHeight: 48, lineHeight: 1.2 },
  demis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 },
  demi: { font: 'inherit', fontSize: 15, fontWeight: 700, border: `1px solid ${C.ligne}`, borderRadius: 8, padding: '10px 12px', background: '#fff', color: C.encre, cursor: 'pointer', minHeight: 52, textAlign: 'left' },
  choisi: { background: C.rdv, borderColor: C.rdv, color: '#fff' },
  bouton: { font: 'inherit', fontSize: 16, fontWeight: 700, border: 0, borderRadius: 8, padding: '12px 18px', background: C.accent, color: '#fff', cursor: 'pointer', minHeight: 48, maxWidth: '100%' },
  boutonDiscret: { font: 'inherit', fontSize: 14, fontWeight: 600, border: `1px solid ${C.ligne}`, borderRadius: 8, padding: '6px 10px', background: '#fff', color: C.encre, cursor: 'pointer', minHeight: 40 },
  info: { marginTop: 14, background: '#e2eff2', borderRadius: 8, padding: '10px 12px', overflowWrap: 'anywhere' },
  alerte: { marginTop: 14, background: '#fff1d9', color: '#7a4a00', borderRadius: 8, padding: '10px 12px', fontWeight: 600 },
  ok: { marginTop: 14, background: '#e1f3e8', color: '#1f5f37', borderLeft: '4px solid #1f7a43', borderRadius: '0 8px 8px 0', padding: '10px 12px', overflowWrap: 'anywhere' },
  erreur: { marginTop: 14, background: '#fbe4e2', color: '#9b2c22', borderRadius: 8, padding: '10px 12px', overflowWrap: 'anywhere' },
  table: { borderCollapse: 'collapse', width: '100%', fontSize: 14 },
  th: { textAlign: 'left', padding: '8px 8px', borderBottom: `1px solid ${C.ligne}`, fontSize: 12, color: C.doux, textTransform: 'uppercase', letterSpacing: '.04em' },
  td: { textAlign: 'left', padding: '8px 8px', borderBottom: `1px solid ${C.ligne}`, verticalAlign: 'top', overflowWrap: 'anywhere' },
};
